import { onBeforeUnmount, ref } from "vue";
import { useTranslations } from "@/composables/useTranslations";

// Keep these in sync with config/rhizom.php `ai_chat.image_upload` and the
// request validation rules. Client-side checks are a UX convenience only — the
// server re-validates every upload.
export const AI_CHAT_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const AI_CHAT_IMAGE_ACCEPT = "image/jpeg,image/png";
const ALLOWED_MIME_TYPES = ["image/jpeg", "image/png"];

/**
 * Single-image attachment handling shared by the full-page and widget composers:
 * one image per message, added via the plus button, paste (CTRL+V) or drag&drop,
 * with a live preview and client-side type/size validation. The object URL used
 * for the preview is created and revoked here so callers never leak it.
 */
export function useAiChatImageUpload() {
    const { t } = useTranslations();

    const image = ref<File | null>(null);
    const previewUrl = ref<string | null>(null);
    const error = ref<string | null>(null);

    const revokePreview = () => {
        if (previewUrl.value !== null) {
            URL.revokeObjectURL(previewUrl.value);
            previewUrl.value = null;
        }
    };

    const clear = () => {
        revokePreview();
        image.value = null;
        error.value = null;
    };

    const setFile = (file: File): boolean => {
        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            error.value = t("vue.ai_chat_image_invalid_type");
            return false;
        }

        if (file.size > AI_CHAT_IMAGE_MAX_BYTES) {
            error.value = t("vue.ai_chat_image_too_large");
            return false;
        }

        revokePreview();
        image.value = file;
        previewUrl.value = URL.createObjectURL(file);
        error.value = null;
        return true;
    };

    // Reject a multi-file selection outright so "one image per message" is explicit.
    const takeSingle = (files: FileList | null | undefined): void => {
        if (!files || files.length === 0) {
            return;
        }

        if (files.length > 1) {
            error.value = t("vue.ai_chat_image_only_one");
            return;
        }

        setFile(files[0]);
    };

    const selectFromInput = (event: Event): void => {
        const input = event.target as HTMLInputElement;
        takeSingle(input.files);
        // Allow re-selecting the same file after a remove.
        input.value = "";
    };

    const handlePaste = (event: ClipboardEvent): void => {
        const items = event.clipboardData?.items;
        if (!items) {
            return;
        }

        for (const item of items) {
            if (item.kind === "file" && item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) {
                    event.preventDefault();
                    setFile(file);
                }
                return;
            }
        }
    };

    const handleDrop = (event: DragEvent): void => {
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            event.preventDefault();
            takeSingle(files);
        }
    };

    onBeforeUnmount(revokePreview);

    return {
        image,
        previewUrl,
        error,
        setFile,
        selectFromInput,
        handlePaste,
        handleDrop,
        clear,
    };
}
